import {
  Box,
  ButtonBase,
  GlobalStyles,
  Tooltip,
  useMediaQuery
} from '@mui/material'
import { keyframes } from '@emotion/react'
import { useRef, useState } from 'react'
import { openMidasPanel } from 'hooks/useMidas'
import kingHead from 'images/midas-king.png'
import {
  LAUNCHER_RIGHT,
  LAUNCHER_SIZE,
  useLauncherBottom
} from './useLauncherBottom'

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'
export const ANIMATED_CLASS = 'midas-launcher-animated'

// Registered so the aura's conic gradient can spin by animating its start
// angle. Browsers without @property show a static gradient (hence the 0deg
// fallback in var()).
const midasAngleProperty = `@property --midas-a {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}`

const spin = keyframes`
  to { --midas-a: 360deg; }
`

// Uses the individual scale/translate properties so the finished animation
// never pins `transform`, which hover and press own.
const happyBounce = keyframes`
  0% { scale: 0.6; translate: 0 24px; opacity: 0; }
  55% { scale: 1.06; translate: 0 -6px; opacity: 1; }
  75% { scale: 0.98; translate: 0 1px; }
  100% { scale: 1; translate: 0 0; }
`

// The launcher remounts on every page; only bounce the first time it appears.
let hasBounced = false

export function resetLauncherBounce(): void {
  hasBounced = false
}

export default function MidasLauncher({
  hidden
}: Readonly<{ hidden: boolean }>) {
  const [bounce] = useState(() => {
    const first = !hasBounced
    hasBounced = true
    return first
  })
  const ref = useRef<HTMLButtonElement>(null)
  const bottom = useLauncherBottom(ref)
  // Mirrors the CSS media query so the spin is not even applied.
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  return (
    <>
      <GlobalStyles styles={midasAngleProperty} />
      <Tooltip title='Ask Midas' placement='left'>
        <ButtonBase
          ref={ref}
          aria-label='Ask Midas'
          aria-haspopup='dialog'
          onClick={openMidasPanel}
          disableRipple
          data-testid='midas-launcher'
          className={reducedMotion ? undefined : ANIMATED_CLASS}
          // Hidden, not unmounted, while the panel is open so the dialog can
          // hand focus back to it on close.
          style={{ bottom, visibility: hidden ? 'hidden' : 'visible' }}
          sx={{
            position: 'fixed',
            right: LAUNCHER_RIGHT,
            zIndex: theme => theme.zIndex.speedDial,
            width: LAUNCHER_SIZE,
            height: LAUNCHER_SIZE,
            borderRadius: '50%',
            overflow: 'visible',
            transition: 'transform .45s cubic-bezier(.2,.9,.3,1.3)',
            animation: bounce ? `${happyBounce} 700ms ease-out both` : 'none',

            // Aura behind the head, taller above than below so the glow sits
            // behind the crown.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-10px -8px 4px -8px',
              borderRadius: '50%',
              zIndex: 0,
              background:
                'conic-gradient(from var(--midas-a, 0deg), #ff4fa6, #d60078 25%, #ff9ed2 45%, #f2c46d 55%, #d92b7e 70%, #ff4fa6)',
              filter: 'blur(12px)',
              opacity: 0.7,
              transition: 'opacity .4s, filter .4s, inset .4s',
              pointerEvents: 'none'
            },
            [`&.${ANIMATED_CLASS}::before`]: {
              animation: `${spin} 7s linear infinite`
            },

            '&:hover, &.Mui-focusVisible': {
              transform: 'translateY(-3px) scale(1.08)'
            },
            '&:hover::before, &.Mui-focusVisible::before': {
              opacity: 0.95,
              filter: 'blur(14px)',
              inset: '-18px -12px 2px -12px'
            },
            [`&.${ANIMATED_CLASS}:hover::before, &.${ANIMATED_CLASS}.Mui-focusVisible::before`]:
              { animationDuration: '2.6s' },
            '&:active': { transform: 'scale(.96)' },
            '&.Mui-focusVisible': {
              outline: '3px solid',
              outlineColor: 'primary.main',
              outlineOffset: 6
            },

            // Reduced motion: no spin, bounce or lift; hover only brightens the
            // aura.
            [REDUCED_MOTION]: {
              animation: 'none',
              transition: 'none',
              '&::before': { animation: 'none', transition: 'opacity .4s' },
              '&:hover, &.Mui-focusVisible, &:active': { transform: 'none' },
              '&:hover::before, &.Mui-focusVisible::before': {
                filter: 'blur(12px)',
                inset: '-10px -8px 4px -8px'
              }
            }
          }}
        >
          <Box
            component='img'
            src={kingHead}
            alt=''
            data-testid='midas-launcher-head'
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 8px rgba(120,0,60,.35))',
              pointerEvents: 'none'
            }}
          />
        </ButtonBase>
      </Tooltip>
    </>
  )
}
